<template>
  <!-- Page length options -->
  <div class="card">
    <div>
      <div
        v-show="onload"
        class="content bg-white"
      >
        <PlaceholderContent is-load="true" />
      </div>
      <div v-if="!onload && !hasPermission">
        <AccessDenied />
      </div>
      <div
        v-show="!onload && hasPermission"
        class="card-header bg-white header-elements-inline"
      >
        <h5 class="card-title">
          {{ $t("_ReportChannel.Sales Order Report by Channel") }}
          <span>
            <a
              style="color: #706f72"
              data-trigger="hover"
              data-popup="popover-suggestion"
              data-placement="right"
              :title="$t('_ReportChannel.Suggestion')"
              :data-content="
                $t(
                  'Summarize all sales orders by sales channel, searched by order date. Consisting of the total of order summary.'
                )
              "
            >
              <i class="fas fa-info-circle" />
            </a>
          </span>
        </h5>
      </div>
      <div
        v-show="!onload && hasPermission"
        class="card-body"
      >
        <div
          v-show="!onload"
          class="card-filter d-flex flex-wrap align-items-center bg-light border-bottom p-3 mt-n3 mx-n3 mb-3"
        >
          <!-- Date filter -->
          <div class="input-group flex-wrap w-100">
            <span class="col-form-label mr-3">{{
              $t("_ReportChannel.Data Duration")
            }}</span>
            <div class="input-group w-auto">
              <span class="input-group-prepend">
                <span class="input-group-text"><i class="fas fa-calendar-alt" /></span>
              </span>
              <DateRangePicker
                style="min-width: 175px"
                opens="right"
                :ranges="ranges"
                v-model="dateRange"
                :locale-data="localedata"
                :control-container-class="'form-control rounded-left-0 rounded-right-0 border-right-0'"
                @update="applyDatePicker"
              >
                <div
                  slot="input"
                  v-html="showTextPicker"
                />
                <div
                  slot="footer"
                  slot-scope="data"
                  class="p-2 drp-buttons"
                >
                  <button
                    @click="cancelDateRange()"
                    v-if="!data.in_selection"
                    type="button"
                    class="applyBtn btn btn-sm btn-secondary"
                  >
                    {{ $t("Cancel") }}
                  </button>
                  <button
                    @click="data.clickApply"
                    v-if="!data.in_selection"
                    type="button"
                    class="applyBtn btn btn-sm btn-primary"
                  >
                    {{ $t("Apply") }}
                  </button>
                </div>
              </DateRangePicker>
              <button
                @click="openAdvanceFilter()"
                type="button"
                class="btn btn-outline-normal rounded-left-0 d-flex align-items-center"
                data-toggle="modal"
                data-target="#advance_filter"
              >
                <i class="fas fa-sliders-v mr-2" />
                <span>{{ $t("_ReportChannel.More Filters") }}</span>
                <span
                  v-if="countFilter"
                  class="badge badge-secondary ml-2"
                >{{
                  countFilter
                }}</span>
              </button>
              <span
                v-if="showTotalFilter"
                class="ml-2 text-blue-800"
                style="padding-top: 8px"
              >
                {{ $t("Filters Results") }}:
                {{ totalFilter | formatNumberNotZero }}
              </span>
            </div>
            <!-- Set PerPage -->
            <!-- <div class="d-flex align-items-center dataTables_length mb-0 ml-auto">
              <div class="dataTables_length ml-auto mb-0">
                <PerPage @per-page="setPerPage" />
              </div>
            </div> -->
          </div>
          <!-- \Date filter -->
        </div>

        <div class="d-flex flex-wrap">
          <div class="card min-width-224 mr-3">
            <div class="card-body">
              <span
                class="rounded-circle badge badge-icon bg-primary position-absolute top-0 right-0 mt-3 mr-3"
              >
                <i class="fas fa-shopping-cart" />
              </span>
              <h6 class="mb-0 font-weight-semibold pr-5">
                {{ $t("_ReportChannel.Orders") }}
              </h6>
              <h3 class="text-grey-800 font-weight-semibold my-1">
                {{ salesOrderSummary.orders | formatNumberDigit }}
              </h3>
            </div>
          </div>
          <div class="card min-width-224 mr-3">
            <div class="card-body">
              <span
                class="rounded-circle badge badge-icon bg-primary position-absolute top-0 right-0 mt-3 mr-3"
              >
                <i class="fas fa-box-taped" />
              </span>
              <h6 class="mb-0 font-weight-semibold pr-5">
                {{ $t("_ReportChannel.Sold QTY") }}
              </h6>
              <h3 class="text-grey-800 font-weight-semibold my-1">
                {{ salesOrderSummary.sold_qty | formatNumberDigit }}
              </h3>
            </div>
          </div>
          <div class="card min-width-224 mr-3">
            <div class="card-body">
              <span
                class="rounded-circle badge badge-icon bg-primary position-absolute top-0 right-0 mt-3 mr-3"
              >
                <!-- <i class="fas fa-calendar-alt" /> -->
                <i class="fas fa-sack-dollar" />
              </span>
              <h6 class="mb-0 font-weight-semibold pr-5">
                {{ $t("_ReportChannel.Total Sales") }}
              </h6>
              <h3 class="text-grey-800 font-weight-semibold my-1">
                ฿{{ salesOrderSummary.total_sales | formatNumber }}
              </h3>
            </div>
          </div>
        </div>

        <OverviewChart :overview-data="charts" />

        <div
          v-show="!onload"
          class="card"
        >
          <div
            :class="['table-responsive mt-0 data-nowrap table-hover table-order']"
            id="table-order"
          >
            <div class="vuetable-wrapper datatable-scroll">
              <Vuetable
                :class="[loadingTable, 'thead-bg-fafafa']"
                class="overflow-x-auto"
                ref="vuetable"
                :no-data-template="$t('No Data Available')"
                :fields="table.fields"
                data-path="data"
                pagination-path=""
                :api-mode="false"
                :data-manager="dataManager"
                @vuetable:loaded="vuetableLoaded"
                @vuetable:loading="vuetableLoading"
                @vuetable:initialized="onInitialized"
              >
                <template slot="tableHeader">
                  <template>
                    <tr>
                      <th
                        colspan="1"
                        style="width: 50px;"
                        class="right aligned"
                      >
                        {{ $t('Ranking') }}
                      </th>
                      <th
                        colspan="1"
                        style="width: 25px;"
                      >
                        {{ $t('Ch.') }}
                      </th>
                      <th
                        colspan="1"
                        style="width: 140px;"
                      >
                        {{ $t('_Integation.Channel Name') }}
                      </th>
                      <th colspan="1">{{ $t('Order') }}</th>
                      <th colspan="1">{{ $t('_Report.QTY') }}</th>
                      <th
                        colspan="1"
                        class="text-right"
                      >
                        {{ $t('Amount') }}
                      </th>
                      <th
                        colspan="1"
                        class="text-right"
                      >
                        {{ $t('_ReportChannel.Shipping') }}
                      </th>
                      <th
                        colspan="1"
                        class="text-right"
                      >
                        {{ $t('COD Fee') }}
                      </th>
                      <th
                        colspan="1"
                        class="text-right"
                      >
                        {{ $t('Discount') }}
                      </th>
                      <th
                        colspan="1"
                        class="text-right"
                      >
                        {{ $t('Total Sales') }}
                        <a
                          style="color: #706f72"
                          data-trigger="hover"
                          data-popup="popover-suggestion"
                          data-placement="right"
                          :title="$t('_ReportChannel.Suggestion')"
                          :data-content="
                            $t(
                              '_ReportChannel.Total sales are calculated as the total amount from that sales channel plus shipping fees.'
                            )
                          "
                        >
                          <i class="fas fa-info-circle" />
                        </a>
                      </th>
                    </tr>
                  </template>
                </template>

                <div
                  slot="ranking"
                  slot-scope="props"
                >
                  <i
                    v-if="props.rowData.i_order == 0"
                    class="fas fa-trophy-alt mr-2 text-orange"
                  />
                  <i
                    v-if="props.rowData.i_order == 1"
                    class="fas fa-trophy-alt mr-2 text-grey-300"
                  />
                  <i
                    v-if="props.rowData.i_order == 2"
                    class="fas fa-trophy-alt mr-2 text-orange-800"
                  />
                  {{ $t('No.') }} {{ props.rowData.i_order + 1 }}
                </div>
                <div
                  slot="ch"
                  slot-scope="props"
                >
                  <div
                    style="
                      white-space: nowrap;
                      max-width: 25px;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                  >
                    <img
                      v-tooltip:top="getTooltip(props.rowData)"
                      :data-original-title="getTooltip(props.rowData)"
                      class="image_box mr-2 rounded"
                      :src="props.rowData.sales_channel_logo"
                    >
                  </div>
                </div>

                <div
                  slot="sales_channel"
                  slot-scope="props"
                >
                  <popper
                    v-if="!props.rowData.sales_channel_status"
                    trigger="hover"
                    :options="{
                      placement: 'top-start',
                      modifiers: {
                        hide: { enabled: false },
                        preventOverflow: { enabled: false },
                      }
                    }"
                  >
                    <div
                      class="popper p-0"
                      style="width:260px"
                    >
                      <div
                        class="header-popup bg-light py-2 px-3"
                        style="border-bottom:1px solid #ddd"
                      >
                        {{ $t('Channel Alert') }}
                      </div>
                      <div class="py-2 px-3 text-wrap">
                        {{ $t('This channel was removed from the store on') }} {{ convertTimezone(props.rowData.row_updated_date) }}
                      </div>
                    </div>
                    <span
                      slot="reference"
                    >
                      <i class="fas fa-triangle-exclamation text-warning-400 mr-1" />
                    </span>
                  </popper>
                  <span 
                    v-if="getChannelName(props.rowData).length > 14"
                    v-tooltip:top="getChannelName(props.rowData)"
                    :data-original-title="getChannelName(props.rowData)"
                  >
                    {{ getChannelName(props.rowData).substr(0, 14) }}...
                  </span>
                  <span v-else>
                    {{ getChannelName(props.rowData) }}
                  </span>
                </div>

                <template slot="tableFooter">
                  <tr style="background:#FAFAFA">
                    <th
                      v-for="(field, indexField) in dataFooterFields"
                      :class="field.dataClass"
                      :key="indexField + '_' + field.visible"
                    >
                      <template v-if="field.name == 'ranking'">
                        {{ $t('Total Amount') }}
                      </template>
                      <template v-else-if="field.name == 'orders'">
                        {{ totalCountOrder | formatNumberDigit }}
                      </template>
                      <template v-else-if="field.name == 'qty'">
                        {{ totalCountQTY | formatNumberDigit }}
                      </template>
                      <template v-else-if="field.name == 'amount'">
                        {{ totalCountAmount | formatNumber }}
                      </template>
                      <template v-else-if="field.name == 'shipping'">
                        {{ totalCountShipping | formatNumber }}
                      </template>
                      <template v-else-if="field.name == 'cod_fee'">
                        {{ totalCountCOD | formatNumber }}
                      </template>
                      <template v-else-if="field.name == 'discount'">
                        {{ totalCountDiscount | formatNumber }}
                      </template>
                      <template v-else-if="field.name == 'total_sales'">
                        {{ totalCountTotalSale | formatNumber }}
                      </template>
                    </th>
                  </tr>
                </template>
              </Vuetable>
            </div>
          </div>
          <!-- <div class="datatable-footer">
            <VuetablePaginationInfo
              class="dataTables_info ml-3"
              ref="paginationInfo"
              :no-data-template="$t('No relevant data')"
              :info-template="$t('Displaying {from} to {to} of {total} items')"
            />
            <div class="dataTables_paginate paging_simple_numbers mb-0 mr-2">
              <VuetablePagination
                ref="pagination"
                pagination-path="pagination"
                @vuetable-pagination:change-page="onChangePage"
              />
            </div>
          </div> -->
        </div>
      </div>
    </div>
    <MoreFilter
      v-if="isAdvanceFilter"
      ref="moreFilter"
      :payment-method-list="paymentMethodList"
      :status-list="statusList"
      @onSubmit="submitMoreFilter"
    />
  </div>
</template>

<script>
import Api from "@/services/endpoint/tenant.service";
import MoreFilter from "./SalesByChannelMoreFilter.vue";
import DateRangePicker from "vue2-daterange-picker";
import PerPage from "../PerPage.vue";
import Select2 from "v-select2-component";
import Popper from 'vue-popperjs';
import Vuetable from "vuetable-2";
import VuetablePagination from "vuetable-2/src/components/VuetablePagination";
import VuetablePaginationInfo from "@/plugins/vuetable-2/VuetablePaginationInfo";
import TableFields from "../assets/js/SaleByChannel_fields.js";
import { showTextPicker } from "../assets/directives/Vuetable.js";
import moment from "moment";
import { convertTimezone } from "@/common/tenancy/convertTimezone";
import numeral from "numeral";
import PlaceholderContent from "@/plugins/PlaceholderContent.vue";
// import {
//         tHeaderShipping,
//         filterValShipping
// } from '../assets/js/xlsx.js';
import AccessDenied from "@/components/tenancy/admin/error-page/templates/AccessDenied.vue";
import OverviewChart from "./chart/salesbychannel/OverviewChart.vue";
import VuetableRowHeader from 'vuetable-2/src/components/VuetableRowHeader.vue';

export default {
  name: "ReportSalesByChannel",
  components: {
    MoreFilter,
    DateRangePicker,
    PerPage,
    // Select2,
    Popper,
    Vuetable,
    VuetablePagination,
    VuetablePaginationInfo,
    PlaceholderContent,
    AccessDenied,
    OverviewChart,
    VuetableRowHeader,
  },
  data() {
    return {
      checkedCount: 0,
      table: {
        fields: TableFields,
      },
      defaultStartDate: moment().subtract(1, "months").format("YYYY-MM-DD 17:00:00"),
      defaultEndDate: moment().format("YYYY-MM-DD 16:59:59"),
      paramsQuery: {
        start: moment().subtract(1, "months").format("YYYY-MM-DD 17:00:00"),
        end: moment().format("YYYY-MM-DD 16:59:59"),
        // limit: 10,
        // page: 1,
        // sort: "",
        payment_method_ids: [],
        order_status: [],
        payment_status: null,
        sales_user_id: null,
      },
      dateRange: {
        startDate: this.defaultStartDate,
        endDate: this.defaultEndDate,
      },
      // amount:{
      //     countShipment: 0,
      //     totalShipping: 0,
      // },
      vuetableFields: false,
      isAdvanceFilter: false,
      countFilter: 0,
      totalFilter: 0,
      // shipmentStatus: [],
      // shippingMethod: [],
      showTotalFilter: false,
      dataFooterFields: [],
      dataTable: [],
      sort: [],
      onload: true,
      hasPermission: true,
      ranges: this.$storeIndex.ranges(),
      localedata: {
        format: "DD-MM-YYYY",
        applyLabel: this.$t("Apply"),
        cancelLabel: this.$t("Cancel"),
        clearable: true,
        monthNames: this.$storeIndex.monthNames(),
        daysOfWeek: this.$storeIndex.daysOfWeek(),
      },
      dateTxtFilter: null,
      loadingTable: null,
      sales_order_summary: {
        orders: 0,
        sold_qty: 0,
        total_sales: 0,
      },
      charts: {
        total_sales: [],
        number_of_orders: [],
        channel_group: [],
      },
      // i: 0,
      // tHeaderShipping: null,
      // filterValShipping: null,
      // list: null,
      // filename: '',
      // autoWidth: true,
      // bookType: 'xlsx',
      // shipmentStatusSetting: {
      //     minimumResultsForSearch: Infinity,
      //     placeholder: this.$t("Shipping Status") + "..",
      // },
      salesOrderSummary: {
        orders: 0,
        sold_qty: 0,
        total_sales: 0,
      },
      paymentMethodList: [],
      statusList: [],
      orderStatusList: [
        {
          id: "pending",
          icon: '<i class="fa-solid fa-clock"></i>',
          name: "_Shipment.Pending",
        },
        {
          id: "processing",
          icon: '<i class="fas fa-sync-alt"></i>',
          name: "Processing",
        },
        {
          id: "completed",
          icon: '<i class="fas fa-check-circle"></i>',
          name: "Completed",
        },
        {
          id: "returned",
          icon: '<i class="fa-solid fa-arrow-rotate-left"></i>',
          name: "_Order.Returned",
        },
        {
          id: "canceled",
          icon: '<i class="fas fa-times-square"></i>',
          name: "Cancel",
        },
      ],
      filterPaymentMethod: null,
    };
  },
  created() {
    // this.tHeaderShipping = tHeaderShipping()
    // this.filterValShipping = filterValShipping()
    this.paramsQuery.order_status = ["pending", "processing", "completed"];
  },
  async mounted() {
    this.dateRange.startDate = this.paramsQuery.start = this.$route.query.start_date
      ? this.$route.query.start_date
      : this.defaultStartDate;
    this.dateRange.endDate = this.paramsQuery.end = this.$route.query.end_date
      ? this.$route.query.end_date
      : this.defaultEndDate;
    // this.onload = true;
  },
  computed: {
    showTextPicker: function () {
      if (
        Object.keys(this.dateRange).includes("startDate") &&
        Object.keys(this.dateRange).includes("endDate") &&
        this.dateRange.startDate != null &&
        this.dateRange.endDate != null
      ) {
        this.dateRange.startDate = convertTimezone.utcToLocal(this.dateRange.startDate);
        this.dateRange.endDate = convertTimezone.utcToLocal(this.dateRange.endDate);
        this.dateTxtFilter = showTextPicker(this.dateRange);
        return this.dateTxtFilter;
      } else {
        return showTextPicker(this.paramsQuery);
      }
    },
    totalCountOrder() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.orders) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountQTY() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.qty) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountAmount() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.amount) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountShipping() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.shipping) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountCOD() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.cod_fee) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountDiscount() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.discount) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountTotalSale() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.total_sales) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    footerTableFields() {
      if (this.vuetableFields !== false) {
        return this.$refs.vuetable.tableFields;
      } else {
        return [];
      }
    },
  },
  methods: {
    dataManager(sortOrder, pagination) {
      if (sortOrder.length > 0) {
        this.paramsQuery.sort = sortOrder
          .map((item) => `${item.sortField}|${item.direction}`)
          .join(",");
      }
      this.setParams();
    },
    onPaginationData(tablePagination) {
      this.$refs.paginationInfo.setPaginationData(tablePagination);
      this.$refs.pagination.setPaginationData(tablePagination);
      // console.log(tablePagination,'tablePagination');
    },
    onInitialized(fields) {
      this.vuetableFields = fields.filter((field) => field.togglable);
    },
    onChangePage(page) {
      this.$refs.vuetable.changePage(page);
    },
    search() {
      this.setParams();
    },
    setPerPage(perPage) {
      this.paramsQuery.page = 1;
      this.$refs.vuetable.currentPage = 1;
      this.paramsQuery.limit = perPage;
      this.setParams();
    },
    openAdvanceFilter() {
      this.isAdvanceFilter = false;
      this.$nextTick(() => {
        this.isAdvanceFilter = true;
      });
    },
    vuetableLoaded() {
      this.onload = false;
    },
    vuetableLoading() {},
    cancelDateRange() {
      this.paramsQuery.start = this.defaultStartDate;
      this.paramsQuery.end = this.defaultEndDate;
      $(".daterangepicker").remove();
      this.setParams();
    },
    applyDatePicker(dateRange) {
      let startDate = this.paramsQuery.start
        ? moment(dateRange.startDate).format("YYYY-MM-DD")
        : null;
      let endDate = this.paramsQuery.end
        ? moment(dateRange.endDate).format("YYYY-MM-DD")
        : null;
      var afterStartDate = startDate + " 00:00:00";
      var afterEndDate = endDate + " 23:59:59";
      this.paramsQuery.start = convertTimezone.localToUtc(afterStartDate);
      this.paramsQuery.end = convertTimezone.localToUtc(afterEndDate);
      // this.paramsQuery.start = moment(afterStartDate).format("YYYY-MM-DD HH:mm:ss");
      // this.paramsQuery.end = moment(afterEndDate).format("YYYY-MM-DD HH:mm:ss");
      this.setParams();
    },
    async setParams() {
      let self = this;
      try {
        this.onload = true;
        // this.paramsQuery.page = this.$refs.vuetable.currentPage;
        let queryString = "";
        if (this.paramsQuery) {
          queryString += $.param(this.paramsQuery);
        }

        let cPaymentMethods = this.paramsQuery.payment_method_ids && this.paramsQuery.payment_method_ids.length ? 1 : 0;
        let cOrderStatus = this.paramsQuery.order_status && this.paramsQuery.order_status.length ? 1 : 0;
        let cSaleUser = this.paramsQuery.sales_user_id && this.paramsQuery.sales_user_id.length ? 1 : 0;
        this.countFilter = cPaymentMethods + cOrderStatus + cSaleUser;

        let result = await Api.ReportV2.getOrderSaleByChannelReport(queryString);

        let totalSales = [];
        let numberOfOrders = [];
        let channelGroup = [];
        this.paymentMethodList = [];
        this.statusList = [];
        let reportData = null;
        let reportDataTable = [];

        if (result.status == 200) {
          reportData = result.data;
          if (result.data && result.data.data && result.data.data.length) {
            // if (!this.paramsQuery.payment_method_ids && !this.paramsQuery.order_status && !this.paramsQuery.payment_status && !this.paramsQuery.sales_user_id.length) {
              result.data.data.map((v, i) => {
                reportDataTable.push({
                  ...v,
                  i_order: i,
                })
              });
              result.data.data = reportDataTable;
            // }
            this.dataTable = result.data;
          } else {
            this.dataTable = [];
          }

          if (result.data && result.data.reports) {
            if (result.data.reports.cards && result.data.reports.cards.total_orders) {
              this.salesOrderSummary.orders = result.data.reports.cards.total_orders.value || 0;
            } else {
              this.salesOrderSummary.orders = 0;
            }
            if (result.data.reports.cards && result.data.reports.cards.total_ordered_quantity) {
              this.salesOrderSummary.sold_qty = result.data.reports.cards.total_ordered_quantity.value || 0;
            } else {
              this.salesOrderSummary.sold_qty = 0;
            }
            if (result.data.reports.cards && result.data.reports.cards.total_sales) {
              this.salesOrderSummary.total_sales = result.data.reports.cards.total_sales.value || 0;
            } else {
              this.salesOrderSummary.total_sales = 0;
            }

            if (result.data.reports.charts && result.data.reports.charts.sales_by_type && result.data.reports.charts.sales_by_type.length) {
              result.data.reports.charts.sales_by_type.map(el => {
                totalSales.push({
                  name: `_ReportSaleChannel.${el.name}`,
                  // name: el.name,
                  value: el.total || 0,
                })
              });
              this.charts.total_sales = totalSales.sort((a, b) => b.value - a.value);
            } else {
              this.charts.total_sales = totalSales;
            }
            if (result.data.reports.charts && result.data.reports.charts.orders_by_type && result.data.reports.charts.orders_by_type.length) {
              result.data.reports.charts.orders_by_type.map(el => {
                numberOfOrders.push({
                  name: `_ReportSaleChannel.${el.name}`,
                  // name: el.name,
                  value: el.orders || 0,
                })
              });
              this.charts.number_of_orders = numberOfOrders.sort((a, b) => b.value - a.value);
            } else {
              this.charts.number_of_orders = numberOfOrders;
            }
            if (result.data.reports.charts && result.data.reports.charts.orders_by_group && result.data.reports.charts.orders_by_group.length) {
              result.data.reports.charts.orders_by_group.map(el => {
                channelGroup.push({
                  name: `_ReportChannel.${el.name}`,
                  // name: el.name,
                  value: el.orders || 0,
                })
              });
              this.charts.channel_group = channelGroup.sort((a, b) => b.value - a.value);
            } else {
              this.charts.channel_group = channelGroup;
            }
          }

          if (result.data && result.data.filter && result.data.filter.payment_method.length) {
            const grouped = _.groupBy(result.data.filter.payment_method, payment => payment.name);
            let allPaymentMethod = [];
            Object.entries(grouped).forEach((entry, index) => {
              const [key, value] = entry;
              let paymentValue = [];
              if (value.length) {
                value.forEach(el => {
                  paymentValue.push(el.id)
                })
                allPaymentMethod.push({
                  name: key,
                  id: index + 1,
                  value: paymentValue,
                })
              }
            });
            this.paymentMethodList = allPaymentMethod;
          }

          if (result.data && result.data.filter && result.data.filter.order_status.length) {
            result.data.filter.order_status.map(status => {
              const findStatus = this.orderStatusList.find(p => p.id == status);
              if (findStatus && findStatus.id) {
                this.statusList.push(findStatus);
              }
            })
          }

          let data = this.$storeIndex.mutations.vueTableFormatJson(result);

          this.$refs.vuetable.setData(data);
          // this.hasPermission = false;
          this.onload = false;
        } else if (result.status != 200) {
          this.onload = false;
          if (result.status === 403) {
            if (result.data.message.indexOf("You can not active plan feature") != -1) {
              this.planExpired = true;
            } else {
              this.hasPermission = false;
            }
          } else if (result.status === 500) {
            this.hasPermission = false;
          }
        }
      } catch (error) {
        // this.$router.push({ name: "t.a.home" });
        console.log(error.response);
        this.dataTable = [];
        this.$refs.vuetable.setData(this.dataTable);
        this.salesOrderSummary = {
          orders: 0,
          sold_qty: 0,
          total_sales: 0,
        },
        this.charts = {
          total_sales: [],
          number_of_orders: [],
          channel_group: [],
        };
        this.onload = false;
      }
    },
    setQueryString(obj) {
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, obj),
        })
        .catch(() => {});
    },
    submitMoreFilter(dataFilter) {
      this.filterPaymentMethod = dataFilter.payment_method_ids;
      const paymentmethod = this.paymentMethodList.find(p => p.id == this.filterPaymentMethod);
      if (paymentmethod && paymentmethod.value) {
        this.paramsQuery.payment_method_ids = paymentmethod.value;
      } else {
        this.paramsQuery.payment_method_ids = [];
        this.filterPaymentMethod = null;
      }
      this.paramsQuery.order_status = dataFilter.order_status;
      // this.paramsQuery.payment_status = dataFilter.payment_status;
      this.paramsQuery.sales_user_id = dataFilter.sales_user_ids;
      this.setParams();
    },
    convertTimezone(date) {
      return date != null && new Date(date) instanceof Date && !isNaN(new Date(date))
        ? convertTimezone.utcToLocal(date)
        : "-";
    },
    getTooltip(rowData) {
      // console.log("🚀 ~ getTooltip ~ rowData:", rowData);
      let channelDetail = "";
      if (rowData.sales_channel_type == "Salepage") {
        if (rowData.salepage_name) {
          channelDetail = " / " + rowData.salepage_name;
        } else {
          channelDetail = " / " + this.$t(`_ReportSaleChannel.${rowData.sales_channel_type}`);
        }
      } else {
        channelDetail = " / " + rowData.sales_channel;
      }
      return this.$t(`_ReportSaleChannel.${rowData.sales_channel_type}`) + channelDetail;
    },
    getChannelName(rowData) {
      if (rowData.sales_channel_type == "Salepage") {
        if (rowData.salepage_name) {
          return this.$t(rowData.salepage_name);
        } else {
          return this.$t(`_ReportSaleChannel.${rowData.sales_channel_type}`);
        }
      } else if (rowData.sales_channel_type == rowData.sales_channel) {
        return this.$t(`_ReportSaleChannel.${rowData.sales_channel}`);
      } else {
        return this.$t(rowData.sales_channel);
      }
    },
  },
  watch: {
    footerTableFields: {
      handler: function (nVal, oVal) {
        let newFooterTableArray = [];
        nVal.forEach((element) => {
          if (element.visible === true) {
            newFooterTableArray.push(element);
          }
        });
        this.$nextTick(() => {
          this.dataFooterFields = newFooterTableArray;
        });
      },
      deep: true,
    },
    onload: function(nval) {
      _componentPopoverCustomHeaderColor();
    },
  },
  filters: {
    formatNumber: function (value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit: function (value) {
      return numeral(value).format("0,0");
    },
  },
};
var _componentPopoverCustomHeaderColor = function() {
  $('[data-popup=popover-suggestion]').popover({
    template: '<div class="popover border-light"><div class="arrow"></div><p class="popover-header bg-light font-weight-bold"></p><div class="popover-body"></div></div>'
  });
};
</script>

<style scoped>
.input-group > .input-group-prepend:not(:first-child) > .input-group-text {
  border-top-left-radius: 0.1875rem;
  border-bottom-left-radius: 0.1875rem;
}
.popover {
  font-family: "Sarabun", sans-serif;
  max-width: 500px;
}
</style>



// WEBPACK FOOTER //
// src/components/tenancy/admin/report/finance/SalesByChannel.vue